import { AfterViewChecked, Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { OrderHistoryService } from './order-history.service';
import {
  CreateOrderHistoryRequestData,
  CreateOrderHistoryResponse,
  OrderHistoryData,
  OrderHistoryResponse,
  UpdateAppHistoryRequestData,
} from './order-history.domain';
import { ErrorService } from '../../../services/error/error.service';
import { AllDropdownKeys } from '../../../jurisdiction/jurisdiction.constants';
import { CommonService } from '../../../services/common.service';
import { LookupOptionsData } from '../../../jurisdiction/jurisdiction.domain';
import { LocalStorageService } from '../../../services/local-storage.service';
import { ConfirmationService } from 'primeng';
import { EnvironmentService } from '../../../services/environment.service';
import { EditorStyle, QuillConfig } from '../../../constants/app.constants';
import { Editor, Quill } from '../../../domains/common.domain';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FileService } from '../../../services/file.service';
import { NetzTranslateService } from '../../../services/netz-translate.service';
import { PLACEHOLDERS } from '../../constants/placeholders';

const maxVisibleLength = 50;

@Component({
  providers: [ConfirmationService, OrderHistoryService],
  selector: 'app-order-history',
  styleUrls: ['./order-history.component.scss'],
  templateUrl: './order-history.component.html',
})
export class OrderHistoryComponent implements OnInit, AfterViewChecked {
  allEventTypes: LookupOptionsData[];
  commentEventType: string;
  eventTypeKey: string;
  isEdit: boolean;
  isLoading: boolean;
  isNewComment: boolean;
  commentToEdit: string;
  orderHistoryData: OrderHistoryData[] = [];
  paginatedOrderHistoryData: OrderHistoryData[] = [];
  paginator: any = {
    first: 0,
    page: 0,
    rows: 30,
  };
  orderHistoryFG: FormGroup;
  selectedLogId: string;
  showCommentButton: boolean;
  technicianId: string;
  editorStyle: Editor;
  quillConfig: Quill;
  placeholders: {};

  @Input() private applicationId: string = '';
  @Input() public pagination: boolean = false;
  @Input() public useDefaultCls: boolean = true;
  @Output() formChange = new EventEmitter<FormGroup>();

  private role: string;

  constructor(
    private appHistoryService: OrderHistoryService,
    private commonService: CommonService,
    private confirmationService: ConfirmationService,
    public environmentService: EnvironmentService,
    private errorService: ErrorService,
    private formBuilder: FormBuilder,
    private localStorageService: LocalStorageService,
    private fileService: FileService,
    private translate: NetzTranslateService
  ) {}

  @HostListener('click', ['$event']) onKeyPress(event) {
    this.fileService.downloadFileFromMessage(event);
  }

  ngOnInit() {
    this.isEdit = false;
    this.isNewComment = false;
    this.commentToEdit = '';
    this.role = this.localStorageService.getItem('role');
    this.selectedLogId = '';
    this.showCommentButton = ['administrator', 'employee', 'observer'].indexOf(this.role) > -1;
    this.commentEventType = 'comment';
    this.technicianId = this.localStorageService.getItem('technician_id') || '';
    this.eventTypeKey = AllDropdownKeys.EventType;
    this.allEventTypes = this.commonService.getDropdownData(this.eventTypeKey);
    this.placeholders = PLACEHOLDERS;
    if (this.environmentService.showWordFormat) {
      this.editorStyle = EditorStyle;
      this.quillConfig = QuillConfig;
    }
    this.orderHistoryFG = this.formBuilder.group({
      newComment: '',
    });
    this.registerForLanguageChange();
    this.getOrderHistoryData();
    this.orderHistoryFG.valueChanges.subscribe(value => this.formChange.emit(this.orderHistoryFG));
  }

  ngAfterViewChecked() {
    const listItems = document.getElementsByTagName('li');
    if (listItems.length) {
      // tslint:disable-next-line:prefer-for-of
      for (let i = 0; i < listItems.length; i++) {
        listItems[i].style.marginLeft = '1.5rem';
      }
    }
  }

  getOrderHistoryData() {
    this.isLoading = true;
    this.appHistoryService.getAppHistoryData(this.applicationId).subscribe(
      (response: OrderHistoryResponse) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, true);
        this.orderHistoryData = response.apphistory.map(item => {
          item.details = this.parsePlaceholders(item.details);
          item.tabSelected = false;
          return item;
        });
        this.paginatedOrderHistoryData = this.pagination
          ? [...this.orderHistoryData.slice(this.paginator.first, this.paginator.first + this.paginator.rows)]
          : this.orderHistoryData;
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, false);
      }
    );
  }

  addNewComment() {
    this.orderHistoryFG.get('newComment').setValidators(Validators.required);
    this.orderHistoryFG.get('newComment').updateValueAndValidity();
    this.isNewComment = true;
  }

  discardNewComment() {
    this.orderHistoryFG.reset();
    this.orderHistoryFG.clearValidators();
    this.orderHistoryFG.updateValueAndValidity();
    this.isNewComment = false;
  }

  discardInlineEdit() {
    this.selectedLogId = '';
    this.commentToEdit = '';
    this.isEdit = false;
  }

  createOrderHistory() {
    this.isLoading = true;
    const requestData: CreateOrderHistoryRequestData = {
      application_id: this.applicationId,
      details: this.orderHistoryFG.value.newComment,
      sk_app_event_type: this.commentEventType,
    };
    this.appHistoryService.createAppHistoryData(requestData).subscribe(
      (response: CreateOrderHistoryResponse) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, true);
        this.getOrderHistoryData();
        this.orderHistoryFG.reset();
        this.orderHistoryFG.clearValidators();
        this.orderHistoryFG.updateValueAndValidity();
        this.isNewComment = false;
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, false);
      }
    );
  }

  deleteComment(event: MouseEvent, logId: string): void {
    event.stopPropagation();
    this.confirmationService.confirm({
      accept: () => {
        this.deleteOrderHistory(logId);
        this.isEdit = false;
      },
      reject: () => {
        this.confirmationService.close();
      },
    });
  }

  editComment(event: MouseEvent, dataObject: OrderHistoryData, i: number) {
    event.stopPropagation();

    var index = this.orderHistoryData.findIndex(item => item.log_id === this.paginatedOrderHistoryData[i].log_id);
    this.orderHistoryData[index].tabSelected = true;
    this.selectedLogId = dataObject.log_id;
    this.commentToEdit = dataObject.details;
    this.isEdit = true;
  }

  updateOrderHistory() {
    this.isLoading = true;
    const requestData: UpdateAppHistoryRequestData = {
      details: this.commentToEdit,
    };
    this.appHistoryService.updateAppHistoryData(this.selectedLogId, requestData).subscribe(
      (response: OrderHistoryResponse) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, true);
        const replaceableIndex = this.orderHistoryData.findIndex(item => item.log_id === this.selectedLogId);
        this.orderHistoryData.splice(replaceableIndex, 1, response.apphistory[0]);
        this.orderHistoryData[replaceableIndex].tabSelected = true;
        this.isEdit = false;
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, false);
      }
    );
  }

  showCommentModifierSection(data: OrderHistoryData): boolean {
    return this.showCommentButton && data.technician_id === this.technicianId && data.sk_app_event_type === this.commentEventType;
  }

  onPageChange(event) {
    this.paginator.first = event.first;
    this.paginator.page = event.page;
    this.paginator.rows = event.rows;
    this.paginatedOrderHistoryData = [...this.orderHistoryData.slice(this.paginator.first, this.paginator.first + this.paginator.rows)];
  }

  private deleteOrderHistory(logId: string) {
    this.isLoading = true;
    this.appHistoryService.deleteAppHistoryData(logId).subscribe(
      (response: OrderHistoryResponse) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, true);
        const removableIndex = this.orderHistoryData.findIndex(item => item.log_id === logId);
        this.orderHistoryData.splice(removableIndex, 1);
      },
      error => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(error, false);
      }
    );
  }

  private parsePlaceholders(text: string) {
    let m;
    const regex = new RegExp('<(?!/?(?:br|b|p|s|strong|em|u|s|ul|li|ol|span|pre|label|a href="|\\a)\\b).+?>', 'gm');
    while ((m = regex.exec(text)) !== null) {
      regex.lastIndex = m.index + 1;
      m.forEach(match => {
        if (this.placeholders[match]) {
          let translation = this.translate.translateString(this.placeholders[match]);
          if (translation?.length > maxVisibleLength) {
            translation = translation.substring(0, maxVisibleLength) + '...';
          }
          text = text.replace(match, translation);
        } else {
          text = text.replace(match, match.replace('<', '').replace('>', ''));
        }
      });
    }
    return text;
  }

  private registerForLanguageChange() {
    this.translate.registerForLanguageChange().subscribe(() => {
      this.getOrderHistoryData();
    });
  }
}
